customElements.define(
  'toggle-element',
  class ToggleElement extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.namespace) {
        console.error('A `data-namespace` attribute is required.');
        return;
      }

      this.setNodeSelectors();
      this.setEventListeners();
      this.setSectionEvents();
      this.setToggleEvents();
    }

    constructor() {
      super();

      if (this.hasAttribute('data-hoist')) {
        document.body.appendChild(this);
      }
    }

    /**
     * Sets the node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        toggles: document.querySelectorAll(`[js-toggle="${this.dataset.namespace}"]`),
      };
    }

    /**
     * Sets the event listeners.
     */
    setEventListeners() {
      [...this.nodeSelectors.toggles].forEach((element) => {
        element.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle();
        });
      });

      theme.eventBus.listen('key:escape', () => this.close());

      /**
       * Section events.
       */
      document.addEventListener('shopify:section:select', ({ detail }) => {
        if (this.hasAttribute('data-section-id')) {
          if (this.dataset.sectionId === detail.sectionId) {
            this.open();
          }
        }
      });

      document.addEventListener('shopify:section:deselect', ({ detail }) => {
        if (this.hasAttribute('data-section-id')) {
          if (this.dataset.sectionId === detail.sectionId) {
            this.close();
          }
        }
      });

      /**
       * Closes this element if another is opening.
       */
      theme.eventBus.listen('ToggleElement:opening', (namespace) => {
        const openingElement = document.querySelector(`toggle-element[data-namespace="${namespace}"]`);

        if ((openingElement && this.contains(openingElement)) || namespace === this.dataset.namespace) {
          return;
        }

        this.close();
      });

      if (this.dataset.event) {
        theme.eventBus.listen(this.dataset.event, () => this.open());
      }
    }

    /**
     * Sets the base toggle events.
     */
    setToggleEvents() {
      theme.eventBus.listen('ToggleElement', (event) => {
        if (!event) {
          this.close();
          return;
        }

        /**
         * If the payload is the namespace, run a simple toggle or close.
         */
        if (typeof event === 'string') {
          if (event === this.dataset.namespace) {
            this.toggle();
            return;
          }

          this.close(!event);
          return;
        }

        /**
         * Otherwise, assume an object has been passed.
         * - This accepts `namespace`, `action` and `transitionend` properties.
         */
        if (event.transitionend) {
          this.addEventListener('transitionend', () => event.transitionend(), { once: true });
        }

        if (event.namespace !== this.dataset.namespace) {
          this.close(false);
          return;
        }

        if (!event.action) {
          this.toggle();
          return;
        }

        switch (event.action) {
          case 'open':
            this.open();
            break;

          case 'close':
            this.close();
            break;
        }
      });
    }

    /**
     * Switches between the active state.
     */
    toggle() {
      if (this.classList.contains('is-active')) {
        this.close();
        return;
      }

      if (this.dataset.excludedPageTypes) {
        const types = this.dataset.excludedPageTypes.split(', ');

        if (types.includes(theme.page_type)) {
          return;
        }
      }

      this.open();
    }

    /**
     * Sets the active state.
     * @param {Boolean} showOverlay - Enables the window overlay.
     */
    open(showOverlay = true) {
      const focusElement = this.querySelector('[js-toggle-element="focusElement"]');

      this.classList.add('is-active');

      if (focusElement) {
        focusElement.focus();

        if (focusElement.tagName === 'INPUT') {
          const value = focusElement.value;

          focusElement.value = '';
          focusElement.value = value;
        }
      }

      theme.eventBus.emit('ToggleElement:opening', this.dataset.namespace);

      if (Boolean(this.dataset.disableOverlay) !== true && showOverlay) {
        theme.eventBus.emit('window-overlay-open', { offset: this.dataset.overlayOffset });
        document.body.classList.add('overflow-hidden');
      }
    }

    /**
     * Removes the active state.
     * @param {Boolean} removeOverlay - Enables hiding the window overlay.
     */
    close(removeOverlay = true) {
      const focusElement = this.querySelector('[js-toggle-element="focusElement"]');

      this.classList.remove('is-active');

      if (focusElement) {
        focusElement.blur();
      }

      if (document.querySelector('toggle-element.is-active')) {
        return;
      }

      if (removeOverlay && !this.hasActiveParentToggleElement()) {
        theme.eventBus.emit('window-overlay-close');
        document.body.classList.remove('overflow-hidden');
      }
    }

    /**
     * Returns if this element has a parent toggle element that is active.
     * @returns {Boolean}
     */
    hasActiveParentToggleElement() {
      return !!this.closest('toggle-element.is-active');
    }

    /**
     * Sets the section events if required.
     */
    setSectionEvents() {
      theme.eventBus.listen('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.namespace) {
          this.open();
        }
      });

      theme.eventBus.listen('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.namespace) {
          this.close();
        }
      });
    }
  }
);
